import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, TrendingDown } from "lucide-react";
import { Reveal } from "./Reveal";
import { waLink } from "@/lib/whatsapp";

const RATES = [15, 20, 27];
const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });

export const Calculator = () => {
    const [revenue, setRevenue] = useState(20000);
    const [rate, setRate] = useState(20);

    const monthly = (revenue * rate) / 100;
    const yearly = monthly * 12;
    const fill = ((revenue - 3000) / (100000 - 3000)) * 100;

    return (
        <section id="cardapio" data-testid="pizzeria-savings-calculator" className="relative py-24 sm:py-32">
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute left-1/2 top-1/2 h-[420px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#0088FF]/[0.07] blur-[130px]" />
            </div>
            <div className="relative mx-auto max-w-5xl px-5 sm:px-8">
                <Reveal>
                    <div className="overflow-hidden rounded-3xl border border-white/[0.08] bg-[#0D121F]/90 backdrop-blur-xl">
                        <div className="h-px bg-gradient-to-r from-transparent via-[#25D366] to-transparent" aria-hidden="true" />
                        <div className="grid gap-10 p-8 sm:p-12 lg:grid-cols-[1.1fr_0.9fr]">
                            <div>
                                <span className="inline-flex items-center gap-2 rounded-full border border-[#25D366]/30 bg-[#25D366]/[0.08] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#25D366]">
                                    <TrendingDown size={13} />
                                    Simulador de Economia
                                </span>
                                <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                                    Quanto o seu negócio perde em taxas todo mês?
                                </h2>
                                <p className="mt-4 text-base text-slate-400">
                                    Restaurante, pizzaria, doceria ou lanchonete: simule
                                    agora o quanto você economiza trocando os marketplaces
                                    pelo Cardápio Próprio da Strondex Tech.
                                </p>

                                <div className="mt-9">
                                    <div className="flex items-center justify-between">
                                        <label htmlFor="calc-revenue" className="text-sm font-semibold text-slate-300">
                                            Faturamento mensal em delivery
                                        </label>
                                        <span data-testid="calc-revenue-value" className="text-lg font-extrabold text-cyan-300">
                                            {brl.format(revenue)}
                                        </span>
                                    </div>
                                    <input
                                        id="calc-revenue"
                                        data-testid="calc-revenue-slider"
                                        type="range"
                                        min="3000"
                                        max="100000"
                                        step="1000"
                                        value={revenue}
                                        onChange={(e) => setRevenue(Number(e.target.value))}
                                        className="calc-range mt-4 w-full"
                                        style={{ "--fill": `${fill}%` }}
                                    />
                                </div>

                                <div className="mt-7">
                                    <span className="text-sm font-semibold text-slate-300">
                                        Comissão do app de entrega
                                    </span>
                                    <div className="mt-3 flex gap-2.5" role="group" aria-label="Taxa de comissão">
                                        {RATES.map((r) => (
                                            <button
                                                key={r}
                                                data-testid={`calc-rate-${r}`}
                                                onClick={() => setRate(r)}
                                                className={`flex-1 rounded-full border py-2.5 text-sm font-bold transition-all duration-300 ${
                                                    rate === r
                                                        ? "border-cyan-400/60 bg-cyan-400/15 text-cyan-300"
                                                        : "border-white/10 bg-white/[0.03] text-slate-400 hover:border-white/25"
                                                }`}
                                            >
                                                {r}%
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col justify-center rounded-2xl border border-[#25D366]/20 bg-[#25D366]/[0.05] p-7 sm:p-9">
                                <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                                    Você entrega aos apps, por mês
                                </span>
                                <motion.span
                                    key={`${revenue}-${rate}`}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.35 }}
                                    data-testid="calc-monthly-loss"
                                    className="mt-2 text-4xl font-extrabold tracking-tight text-[#25D366] sm:text-5xl"
                                >
                                    {brl.format(monthly)}
                                </motion.span>
                                <div className="mt-5 border-t border-white/[0.08] pt-5">
                                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">
                                        Em um ano, isso vira
                                    </span>
                                    <p data-testid="calc-yearly-loss" className="mt-1.5 text-2xl font-extrabold text-slate-100">
                                        {brl.format(yearly)}
                                    </p>
                                </div>
                                <p className="mt-5 text-sm leading-relaxed text-slate-400">
                                    Com o cardápio digital próprio, esse valor fica no seu
                                    caixa: <b className="text-slate-200">não cobramos taxa nem comissão
                                    alguma</b> — apenas o trabalho de desenvolver o projeto.
                                    Os pedidos chegam montados direto no seu WhatsApp.
                                </p>
                                <a
                                    href={waLink("cardapio")}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="calc-whatsapp-button"
                                    className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 text-sm font-bold text-[#052e12] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#2ce06f] hover:shadow-[0_10px_36px_rgba(37,211,102,0.35)]"
                                >
                                    <MessageCircle size={17} strokeWidth={2.5} />
                                    Quero Parar de Pagar Taxas
                                </a>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
